import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { ViewSwitch } from '../components/ViewSwitch';
import { cn } from '../lib/utils';
import { getProductDisplayName, getUrlsFromJsonb } from '../lib/helpers';
import { toast } from 'sonner';
import { 
  Search, 
  Loader2,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function ProductResearch() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [winningDetails, setWinningDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');
  const { t } = useTranslation();

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, reviewsRes, winningRes] = await Promise.all([
        supabase.from('products').select('*').order('created_at', { ascending: false }),
        supabase.from('product_reviews').select('*'),
        supabase.from('winning_product_details').select('ecom_id, product_name'),
      ]);

      if (productsRes.error) throw productsRes.error;
      setProducts(productsRes.data || []);
      setReviews(reviewsRes.data || []);
      setWinningDetails(winningRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load research data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getReviewForProduct = (ecomId) => reviews.find(r => r.ecom_id === ecomId);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="product-research-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1E1B4B] font-['Manrope']">
              {t('research.title')}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {t('research.subtitle', { products: products.length, reviews: reviews.length })}
            </p>
          </div>
          <ViewSwitch view={view} onViewChange={setView} />
        </div>

        {products.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Search className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {t('research.emptyTitle')}
            </h3>
            <p className="text-slate-500">
              {t('research.emptySubtitle')}
            </p>
          </div>
        ) : view === 'grid' ? (
          // Grid View
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((product) => {
              const review = getReviewForProduct(product.ecom_id);
              const displayName = getProductDisplayName(product.ecom_id, winningDetails);
              const images = getUrlsFromJsonb(product.image_urls);
              const heroImage = images[0];

              return (
                <div
                  key={product.ecom_id}
                  onClick={() => navigate(`/product-research/${product.ecom_id}`)}
                  className={cn(
                    'bg-white rounded-xl border border-slate-200 overflow-hidden cursor-pointer',
                    'hover:shadow-lg hover:border-cyan-300 transition-all group'
                  )}
                  data-testid={`product-card-${product.ecom_id}`}
                >
                  {/* Image */}
                  <div className="aspect-square bg-slate-100 overflow-hidden">
                    {heroImage ? (
                      <img 
                        src={heroImage} 
                        alt={displayName} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Search className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="p-3">
                    <h3 className="font-semibold text-[#1E1B4B] text-sm line-clamp-2 mb-2">
                      {displayName}
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={product.product_status} lane="research" />
                      {review && (
                        <span className="text-xs text-slate-500">
                          {t('research.reviewsBadge')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // List View
          <div className="space-y-3">
            {products.map((product) => {
              const review = getReviewForProduct(product.ecom_id);
              const displayName = getProductDisplayName(product.ecom_id, winningDetails);
              const images = getUrlsFromJsonb(product.image_urls);
              const heroImage = images[0];

              return (
                <div
                  key={product.ecom_id}
                  onClick={() => navigate(`/product-research/${product.ecom_id}`)}
                  className={cn(
                    'bg-white rounded-xl border border-slate-200 overflow-hidden cursor-pointer',
                    'hover:shadow-lg hover:border-cyan-300 transition-all group'
                  )}
                  data-testid={`product-row-${product.ecom_id}`}
                >
                  <div className="flex items-center gap-4 p-4">
                    {/* Image */}
                    <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                      {heroImage ? (
                        <img src={heroImage} alt={displayName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Search className="w-6 h-6 text-slate-300" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[#1E1B4B] truncate">{displayName}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <StatusBadge status={product.product_status} lane="research" />
                        {review && (
                          <StatusBadge status={review.review_status} lane="research" />
                        )}
                        <span className="text-xs text-slate-500 capitalize">{product.platform}</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-500 transition-colors flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
